import config from '../../config';
import { authHeader } from '../_helpers';
import { apiFetch } from './apiFetch';
import { applyCompanyPermissionsToUser } from '../utils/companyPermissionFlags';
import { authenticatedIdentity, readSession, saveSession, clearSession, recoverSession } from './webSession';
import {
    readAccounts, saveAccount, removeAccount, markPasswordRequired,
    markAllPasswordRequired, setPreference, touchAccount,
} from './savedAccounts';
import { withRememberedLoginLock } from './rememberedLoginLock';

async function webRequest(path, body = {}, token) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
        const response = await fetch(`${config.apiUrl}/users/web/${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) {
            throw Object.assign(new Error(data.message || 'The account request failed.'), {
                status: response.status, code: data.code, retryable: data.retryable,
            });
        }

        return data;
    } catch (error) {
        if (error.name === 'AbortError') throw new Error('The request timed out. Please retry.');

        throw error;
    } finally {
        clearTimeout(timeout);
    }
}

async function acceptLogin(response, expectedIdentity) {
    if (!response.user?.username) throw new Error('The server returned an invalid account.');

    // Company name is display metadata; permissions come from the authenticated response.
    const displayController = new AbortController();
    const displayTimeout = setTimeout(() => displayController.abort(), 3000);
    try {
        const company = await fetch(`${config.apiUrl}/getAccountTarget?account_id=${encodeURIComponent(response.user.accountId)}`, {
            signal: displayController.signal,
        });
        const account = company.ok ? await company.json() : null;
        if (account?.companyName) response.user.companyName = account.companyName;
    } catch {
        // A display lookup does not prevent a valid authenticated login.
    } finally {
        clearTimeout(displayTimeout);
    }

    let user;
    try {
        user = saveSession(response, expectedIdentity);
    } catch (error) {
        // A late response must not restore an account cleared in another tab.
        await webRequest('logout', {}, response.token).catch(() => {});
        throw error;
    }

    try {
        touchAccount(user.id, user);
    } catch {
        // Optional history updates must not revoke a valid login.
    }
    return user;
}

async function login(username, password) {
    const expectedIdentity = authenticatedIdentity();
    return acceptLogin(await webRequest('login', { username, password }), expectedIdentity);
}

async function loginRemembered(account) {
    const initialIdentity = authenticatedIdentity();
    return withRememberedLoginLock(account.selector, async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            const current = readSession();
            if (current && String(current.id) === String(account.id) && authenticatedIdentity() !== initialIdentity) return current;
            if (authenticatedIdentity() !== initialIdentity) throw new Error('The account changed. Please try again.');

            try {
                const response = await webRequest('remembered-login', { selector: account.selector });
                return await acceptLogin(response, initialIdentity);
            } catch (error) {
                if (error.code === 'remembered_login_superseded') {
                    if (attempt === 0) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                        continue;
                    }

                    throw new Error('Another tab is signing in. Please retry.');
                }
                if (error.code === 'invalid_remembered_login' && error.status === 401 && authenticatedIdentity() === initialIdentity) {
                    markPasswordRequired(account.id, account.selector);
                }

                throw error;
            }
        }
    });
}

async function logout(expectedIdentity = authenticatedIdentity()) {
    const user = readSession();
    if (expectedIdentity !== authenticatedIdentity()) return;

    try {
        if (user?.token) {
            await webRequest('logout', {}, user.token);
        } else if (user?.id) {
            await fetch(`${config.apiUrl}/users/logout`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: user.id, clientType: 'web' }),
            });
        }
    } catch {
        // Local logout still completes when the server is unavailable.
    } finally {
        clearSession(expectedIdentity);
    }
}

async function forgetAccount(account) {
    if (account.selector) {
        try {
            await webRequest('forget', { selector: account.selector }, readSession()?.token);
        } catch (error) {
            // 401/403 means this browser's cookie no longer matches the grant, so it can never
            // be used from here; the grant expires on the server. Keep network/server failures retryable.
            if (![401, 403].includes(error.status)) throw error;
        }
    }

    removeAccount(account.id);
}

async function changeSavePreference(mode) {
    if (mode !== 'one_click' && readAccounts().some(account => account.selector)) {
        await webRequest('forget-all');
        markAllPasswordRequired();
    }

    setPreference(mode);
}

async function saveCurrentAccount(user, mode, replacementId, expectedIdentity = authenticatedIdentity()) {
    // A legacy Basic session's stored password must never mint a remembered login;
    // one-click becomes available after the account's next web-token login.
    const savedMode = mode === 'one_click' && !user?.token ? 'password' : mode;
    if (savedMode !== 'one_click') return saveWithCurrentSession(user, savedMode, replacementId, expectedIdentity);

    // Tabs share this active session; serialize grant creation/renewal per session.
    return withRememberedLoginLock(user.sessionSelector, () => saveWithCurrentSession(user, savedMode, replacementId, expectedIdentity));
}

export const ONE_CLICK_UNAVAILABLE = 'One-click login couldn’t be saved on this browser, so this account was saved with password required.';

async function saveWithCurrentSession(user, mode, replacementId, expectedIdentity) {
    const current = readSession();
    if (!current || expectedIdentity !== authenticatedIdentity() || String(current.id) !== String(user?.id)
        || current.token !== user.token || current.authdata !== user.authdata) {
        throw new Error('The account changed. Please try again.');
    }
    const accounts = readAccounts();
    const existing = accounts.find(account => account.id === String(user.id));
    const replacement = accounts.find(account => account.id === String(replacementId));
    if (mode !== 'none' && !existing && accounts.length >= 5 && !replacement) {
        throw new Error('Choose a saved account to replace.');
    }

    try {
        if (mode === 'one_click') {
            const token = user.token;
            if (expectedIdentity !== authenticatedIdentity()) throw new Error('The account changed. Please try again.');

            const grant = await webRequest('remember', { replacementSelector: replacement?.selector || undefined }, token);
            if (expectedIdentity !== authenticatedIdentity()) {
                await webRequest('forget', { selector: grant.selector }, token);
                throw new Error('The account changed. Please try again.');
            }

            // Confirm the browser kept the cookie; otherwise one-click would fail at next login.
            const check = await webRequest('remember-check', { selector: grant.selector }, token);
            if (!check.present) {
                await webRequest('forget', { selector: grant.selector }, token).catch(() => {});
                if (expectedIdentity !== authenticatedIdentity()) throw new Error('The account changed. Please try again.');

                saveAccount(user, 'password', null, replacementId);
                return { notice: ONE_CLICK_UNAVAILABLE };
            }
            saveAccount(user, mode, grant.selector, replacementId);
        } else {
            if (replacement?.selector) await webRequest('forget', { selector: replacement.selector }, user.token);
            if (existing?.selector) await webRequest('forget', { selector: existing.selector }, user.token);
            if (expectedIdentity !== authenticatedIdentity()) throw new Error('The account changed. Please try again.');

            if (mode === 'password') saveAccount(user, mode, null, replacementId);
            if (mode === 'none' && existing) markPasswordRequired(existing.id);
        }
    } catch (error) {
        if (error.status === 401 && error.code === 'invalid_session') recoverSession(expectedIdentity);

        throw error;
    }
}

export async function refreshCompanyPermissions() {
    const session = localStorage.getItem('user');
    const user = readSession();
    if (!user) return;

    const permissions = await hasPermission(user.accountId);
    if (!Array.isArray(permissions) || localStorage.getItem('user') !== session) return;

    localStorage.setItem('user', JSON.stringify(applyCompanyPermissionsToUser(user, permissions)));
}

async function hasPermission(accountId) {
    try {
        const response = await fetch(`${config.apiUrl}/checkPermission?account_id=${encodeURIComponent(accountId)}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
        });
        return response.ok ? await response.json() : null;
    } catch {
        return null;
    }
}

async function getAll() {
    const response = await apiFetch(`${config.apiUrl}/users`, { headers: authHeader() });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || response.statusText);

    return data;
}

export const userService = {
    login,
    loginRemembered,
    logout,
    getAll,
    hasPermission,
    forgetAccount,
    changeSavePreference,
    saveCurrentAccount,
};
